import { Box, Button, Stack, Typography } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import theme, { shadows } from "../../../../theme";
import IconLoader from "../IconLoader";
import { convertPxToRem } from "../../../../utils";

interface ItemPillProps {
  onClick: () => void;
  selectedItem: Record<string, any>;
  itemMap: Record<string, any>;
  shouldShowBody?: boolean;
  shouldShowSubtitle?: boolean;
  titleStyles?: any;
  shouldShowArrow?: boolean;
  shouldShowPrice?: boolean;
  shouldShowSecondarySubtitle?: boolean;
  shouldShowDiscount?: boolean;
}

export default function ItemPill({
  onClick,
  selectedItem,
  itemMap,
  shouldShowBody = true,
  shouldShowSubtitle = false,
  titleStyles,
  shouldShowArrow = true,
  shouldShowPrice = false,
  shouldShowSecondarySubtitle = false,
  shouldShowDiscount = false,
}: ItemPillProps) {
  const { t } = useTranslation();

  const data = itemMap[selectedItem.panelChargeCode || selectedItem.name];

  return (
    <>
      {data ? (
        <Button
          key={data.title}
          variant="contained"
          color="info"
          aria-label={t(data.title)}
          sx={{
            justifyContent: "flex-start",
            flexDirection: "column",
            boxShadow: shadows.lightest,
            padding: 1,
            "&:hover, &:focus": {
              outline: "none",
              boxShadow: shadows.lightest,
              backgroundColor: theme.palette.primary[100],
            },
          }}
          fullWidth
          onClick={onClick}
        >
          <Stack
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            width="100%"
          >
            <Stack
              display="flex"
              flexDirection="row"
              alignItems="center"
              gap={2}
            >
              <Stack alignSelf="start">{data.icon}</Stack>
              <Stack textAlign="left" style={{ marginTop: 0 }}>
                <Typography
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    ...titleStyles,
                  }}
                >
                  {t(data.title)}
                </Typography>

                {data?.subtitle && shouldShowSubtitle && (
                  <Typography variant="body2">{t(data.subtitle)}</Typography>
                )}

                {data?.secondarySubtitle && shouldShowSecondarySubtitle && (
                  <Typography fontSize={convertPxToRem(9)}>
                    {t(data.secondarySubtitle)}
                  </Typography>
                )}

                {shouldShowBody && (
                  <>
                    <Typography mt={0.5} fontSize={convertPxToRem(12)}>
                      <Trans i18nKey={data.body} />
                    </Typography>
                  </>
                )}

                {shouldShowPrice && (
                  <>
                    {shouldShowDiscount ? (
                      <Box
                        width="fit-content"
                        px={2}
                        py={0.5}
                        sx={{
                          backgroundColor: theme.palette.accent4.main,
                          borderRadius: 2,
                          marginTop: 1,
                        }}
                      >
                        <Typography
                          fontWeight="bold"
                          color="primary.contrastText"
                          textAlign="left"
                        >
                          {selectedItem.amountTotalFormatted}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="primary.contrastText"
                          textAlign="left"
                          sx={{
                            textDecoration: "line-through",
                          }}
                        >
                          {t("common.discount.prefix", {
                            amountFormatted:
                              selectedItem.originalAmountFormatted,
                          })}
                        </Typography>
                      </Box>
                    ) : (
                      <Box
                        width="fit-content"
                        px={2}
                        py={0.5}
                        sx={{
                          backgroundColor: theme.palette.primary.light,
                          borderRadius: 2,
                          marginTop: 1,
                        }}
                      >
                        <Typography
                          fontWeight="bold"
                          color="primary.contrastText"
                          textAlign="center"
                        >
                          {selectedItem.amountTotalFormatted}
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </Stack>
            </Stack>
            {shouldShowArrow && <IconLoader icon="ChevronNextIcon" />}
          </Stack>
        </Button>
      ) : null}
    </>
  );
}
